import React, { useEffect, useState } from 'react';
import { Lock, Save, Loader2, CreditCard, ExternalLink, ShieldCheck, Globe, Wand2 } from 'lucide-react';
import api from '../services/api';
import { useVendorContext } from './VendorAdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import MarketplaceSetupPanel from './MarketplaceSetupPanel';
import PreferredDriverPicker from './PreferredDriverPicker';
import DigestPreferencePanel from './DigestPreferencePanel';
import PaystackSetupPanel from './PaystackSetupPanel';
import StoreLocationPanel from './StoreLocationPanel';

const VendorSettingsPage = () => {
  const { vendor_id, impersonating } = useVendorContext();
  const { user } = useAuth();
  const isOAuth = user?.provider === 'google';
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, password: '' });
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [vendorMeta, setVendorMeta] = useState(null);  // {country, state} from vendor doc
  const [shippingSizes, setShippingSizes] = useState([]);  // 7-tier ladder from platform_settings

  const load = async () => {
    const r = await api.get('/vendor-admin/settings', { params: impersonating ? { vendor_id } : undefined });
    setData(r.data); setDraft({});
    // Fetch the vendor doc to know the country (for auto-detect).
    try {
      const v = await api.get(`/vendors/${vendor_id || r.data._meta?.vendor_id}`).then((res) => res.data);
      setVendorMeta({ country: v.country, state: v.state, currency: v.currency, category: v.category });
    } catch (e) { /* */ }
    try {
      const c = await api.get('/vendor-admin/currency-suggestion', {
        params: { country: vendorMeta?.country || undefined },
      }).then((res) => res.data);
      setCurrencyOptions(c.options || []);
    } catch (e) { /* */ }
    try {
      const s = await api.get('/shipping-sizes').then((res) => res.data);
      setShippingSizes(s.sizes || []);
    } catch (e) { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendor_id]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const get = (k) => (k in draft ? draft[k] : (data?.[k] || ''));

  const autoDetectCurrency = async () => {
    if (!vendorMeta?.country) {
      toast.error('Country not set on your store yet.');
      return;
    }
    try {
      const r = await api.get('/vendor-admin/currency-suggestion', {
        params: { country: vendorMeta.country },
      }).then((res) => res.data);
      if (r.code) {
        set('currency', r.code);
        toast.success(`Detected ${r.code} (${r.symbol}) for ${r.country}`);
      } else {
        toast.warning(`No currency match for ${vendorMeta.country}. Defaulting to USD.`);
        set('currency', 'USD');
      }
    } catch (e) { toast.error('Detection failed'); }
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const pwd = isOAuth && !confirm.password ? 'I_CONFIRM' : confirm.password;
      await api.patch('/vendor-admin/settings', { updates: draft, password: pwd }, {
        params: impersonating ? { vendor_id } : undefined,
      });
      toast.success('Settings saved');
      setConfirm({ open: false, password: '' });
      setEditing(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const connectStripe = async () => {
    setConnecting(true);
    try {
      const { data: r } = await api.post('/vendor-admin/stripe/connect-link', { return_url: window.location.href });
      window.location.href = r.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Stripe Connect is unavailable');
    } finally { setConnecting(false); }
  };

  if (!data) return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  const hasAcct = !!data.stripe_account_id;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Store settings</h1>
          <p className="text-slate-500 mt-1">Stripe Connect & payout configuration. Password required to save.</p>
        </div>
        {editing ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setDraft({}); setEditing(false); }} data-testid="vendor-settings-cancel-btn">Cancel</Button>
            <Button onClick={() => setConfirm({ open: true, password: '' })} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="vendor-settings-save-btn">
              <Save className="h-4 w-4 mr-1.5" />Save
            </Button>
          </div>
        ) : (
          <Button onClick={() => setEditing(true)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="vendor-settings-edit-btn">Edit</Button>
        )}
      </div>

      <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="h-5 w-5 text-emerald-600" />
          <h2 className="font-semibold text-slate-900">Payment processor</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Choose how customers pay for orders from your store. Stripe is best for USD/CAD/EUR; Paystack is best for NGN/GHS/KES/ZAR.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {['stripe', 'paystack'].map((p) => {
            const active = (get('payment_processor') || 'stripe') === p;
            return (
              <button
                key={p}
                type="button"
                onClick={() => { if (!editing) setEditing(true); set('payment_processor', p); }}
                className={`text-left rounded-xl border p-4 transition
                  ${active
                    ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}`}
                data-testid={`payment-processor-${p}`}
                aria-pressed={active}
              >
                <div className={`font-semibold capitalize ${active ? 'text-emerald-800' : 'text-slate-900'}`}>{p}</div>
                <div className={`text-xs mt-1 ${active ? 'text-emerald-700' : 'text-slate-500'}`}>
                  {p === 'stripe' ? 'Card checkout · Connect payouts · USD/CAD/EUR' : 'Card + bank transfer · Subaccount splits · NGN/GHS/KES/ZAR'}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {shippingSizes.length > 0 && (
        <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="default-shipping-size-section">
          <div className="flex items-center gap-2 mb-1">
            <CreditCard className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Default parcel size</h2>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            Override the platform&apos;s category default. Picking a smaller box (e.g. a bakery in food)
            reduces the auto-computed shipping fee for every order at your store.
          </p>
          <select
            value={get('default_shipping_size_id') || ''}
            onChange={(e) => { if (!editing) setEditing(true); set('default_shipping_size_id', e.target.value); }}
            className="h-10 w-full max-w-md rounded-md border border-slate-200 bg-white px-3 text-sm"
            data-testid="default-shipping-size-select"
          >
            <option value="">— use platform default for my category —</option>
            {shippingSizes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · up to {s.max_weight_kg}kg (×{Number(s.price_multiplier).toFixed(1)})
              </option>
            ))}
          </select>
        </section>
      )}

      <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Stripe payments</h2>
            {hasAcct && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">
                <ShieldCheck className="h-3 w-3" /> Connected
              </span>
            )}
          </div>
          <Button onClick={connectStripe} disabled={connecting} variant={hasAcct ? 'outline' : 'default'}
            className={hasAcct ? '' : 'bg-[#635BFF] hover:bg-[#4f47e6] text-white'} data-testid="connect-stripe-btn">
            {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-1.5" />}
            {hasAcct ? 'Re-onboard with Stripe' : 'Connect with Stripe'}
          </Button>
        </div>
        <div className="divide-y divide-slate-100">
          <Row label="Stripe Connect account ID" hint="acct_xxx — receives destination payouts (10% platform fee)" value={get('stripe_account_id')} onChange={(v) => set('stripe_account_id', v)} disabled={!editing} placeholder="acct_1Nx..." />
          <Row label="Stripe publishable key (optional)" hint="Override platform key if you charge directly" value={get('stripe_publishable_key')} onChange={(v) => set('stripe_publishable_key', v)} disabled={!editing} placeholder="pk_live_..." />
          <Row label="Payout email" hint="Where new-order notifications & receipts are sent" value={get('payout_email')} onChange={(v) => set('payout_email', v)} disabled={!editing} placeholder="finance@store.com" />
          <Row label="Interac e-transfer email" hint="Customers see this on checkout when they pick E-transfer for the goods invoice" value={get('interac_email')} onChange={(v) => set('interac_email', v)} disabled={!editing} placeholder="pay@yourstore.ca" />
          <Row label="WhatsApp number" hint="With country code. Customers can tap to notify you when they send an e-transfer." value={get('whatsapp_number')} onChange={(v) => set('whatsapp_number', v)} disabled={!editing} placeholder="+14165551234" />
          <Row label="Tax ID / EIN" hint="For 1099 / VAT reporting" value={get('tax_id')} onChange={(v) => set('tax_id', v)} disabled={!editing} placeholder="" />
        </div>
      </section>

      <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Globe className="h-5 w-5 text-emerald-600" />
          <h2 className="font-semibold text-slate-900">Currency</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start py-1">
          <div>
            <Label className="text-sm text-slate-700 font-medium">Store currency</Label>
            <div className="text-[11px] text-slate-400 mt-0.5">
              All prices on your store are charged in this currency. Customers see this currency at checkout.
              {vendorMeta?.country && (
                <span className="block mt-0.5">Your store country: <span className="font-medium text-slate-600">{vendorMeta.country}</span></span>
              )}
            </div>
          </div>
          <div className="sm:col-span-2 flex items-center gap-2">
            <select value={get('currency') || vendorMeta?.currency || 'USD'}
              onChange={(e) => set('currency', e.target.value)}
              disabled={!editing}
              className="h-10 w-full max-w-xs rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="vendor-currency-select">
              {currencyOptions.map((c) => (
                <option key={c.code} value={c.code}>{c.code} — {c.symbol} {c.name}</option>
              ))}
            </select>
            <Button size="sm" variant="outline"
              onClick={() => { if (!editing) setEditing(true); autoDetectCurrency(); }}
              data-testid="auto-detect-currency-btn">
              <Wand2 className="h-3.5 w-3.5 mr-1" /> Auto-detect
            </Button>
          </div>
        </div>
      </section>

      <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-semibold text-slate-900">Storefront language</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start py-1">
          <div>
            <Label className="text-sm text-slate-700 font-medium">Default storefront language</Label>
            <div className="text-[11px] text-slate-400 mt-0.5">Customers visiting your store will see this language by default.</div>
          </div>
          <div className="sm:col-span-2">
            <select value={get('default_language') || 'en'} onChange={(e) => set('default_language', e.target.value)} disabled={!editing}
              className="h-10 w-full max-w-xs rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="vendor-default-language-select">
              <option value="en">🇺🇸 English</option>
              <option value="es">🇪🇸 Español</option>
              <option value="fr">🇫🇷 Français</option>
              <option value="pt">🇧🇷 Português</option>
              <option value="ar">🇸🇦 العربية</option>
            </select>
          </div>
        </div>
      </section>

      <MarketplaceSetupPanel vendorId={vendor_id} impersonating={impersonating} />
      <StoreLocationPanel vendorId={vendor_id} impersonating={impersonating} />
      <PreferredDriverPicker vendorId={vendor_id} impersonating={impersonating} />
      {(get('payment_processor') || 'stripe') === 'paystack' && (
        <PaystackSetupPanel vendorId={vendor_id} impersonating={impersonating} />
      )}
      <DigestPreferencePanel vendorId={vendor_id} impersonating={impersonating} />

      <Dialog open={confirm.open} onOpenChange={(o) => setConfirm((c) => ({ ...c, open: o }))}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-emerald-600" />Confirm with password</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">For security, please {isOAuth ? 'confirm by typing the phrase below' : 're-enter your password'}.</p>
          <div className="mt-3">
            {isOAuth ? (
              <>
                <Label className="text-xs text-slate-600">Type <span className="font-mono font-semibold">I_CONFIRM</span> to proceed</Label>
                <Input value={confirm.password} onChange={(e) => setConfirm((c) => ({ ...c, password: e.target.value }))} className="h-10 mt-1" placeholder="I_CONFIRM" data-testid="vendor-confirm-password-input" />
              </>
            ) : (
              <>
                <Label className="text-xs text-slate-600">Account password</Label>
                <Input type="password" value={confirm.password} onChange={(e) => setConfirm((c) => ({ ...c, password: e.target.value }))} className="h-10 mt-1" autoFocus data-testid="vendor-confirm-password-input" />
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm({ open: false, password: '' })}>Cancel</Button>
            <Button onClick={doSave} disabled={saving || !confirm.password} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="vendor-confirm-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-1.5" />}
              Confirm & save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Row = ({ label, hint, value, onChange, disabled, placeholder }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-start py-3">
    <div>
      <Label className="text-sm text-slate-700 font-medium">{label}</Label>
      {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
    <div className="sm:col-span-2">
      <Input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder={placeholder} className="h-10" />
    </div>
  </div>
);

export default VendorSettingsPage;
